import React, { useEffect, useState, useRef } from 'react';
import IkIslemleriAPI, { PerIzinRaporBilgiListRequest } from '../../../apis/IkIslemleriAPI';
import TableComponent from "../../../components/OB/TableComponent";

interface IzinVeRaporlarProps {
    qtSicilId: number;
    personelTipi: string;
}

const IzinVeRaporlar: React.FC<IzinVeRaporlarProps> = ({ qtSicilId, personelTipi }) => {
    const [izinRaporList, setIzinRaporList] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    type SortField = "ctTur" | "startDate" | null;

    const [sortField, setSortField] = useState<SortField>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // API istek kontrolü - aynı anda birden fazla istek engelleme
    const isLoadingRef = useRef(false);
    // Son yüklenen sicilId - gereksiz tekrar yüklemeyi önle
    const lastLoadedSicilRef = useRef<number | null>(null);

    const sortedData = React.useMemo(() => {
        if (!sortField) return izinRaporList;

        return [...izinRaporList].sort((a, b) => {
            let valA: any;
            let valB: any;

            if (sortField === "ctTur") {
                valA = (a.ctTur ?? "").toString();
                valB = (b.ctTur ?? "").toString();

                return sortDirection === "asc"
                    ? valA.localeCompare(valB, 'tr')
                    : valB.localeCompare(valA, 'tr');
            }

            if (sortField === "startDate") {
                valA = new Date(a._dtBaslangicTs).getTime();
                valB = new Date(b._dtBaslangicTs).getTime();

                return sortDirection === "asc"
                    ? valA - valB
                    : valB - valA;
            }

            return 0;
        });
    }, [izinRaporList, sortField, sortDirection]);

    const renderSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return <span style={{ marginLeft: 6, opacity: 0.3 }}>⇅</span>;
        }

        return sortDirection === "asc"
            ? <span style={{ marginLeft: 6 }}>▲</span>
            : <span style={{ marginLeft: 6 }}>▼</span>;
    };

    const columns = [
        {
            header: (
                <div
                    onClick={() => {
                        setSortField("ctTur");
                        setSortDirection(prev =>
                            sortField === "ctTur" && prev === "asc" ? "desc" : "asc"
                        );
                    }}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none"
                    }}
                >
                    İzin & Rapor Türü
                    {renderSortIcon("ctTur")}
                </div>
            ) as any,
            field: "ctTur",
            className: "text-center"
        },
        {
            header: (
                <div
                    onClick={() => {
                        setSortField("startDate");
                        setSortDirection(prev =>
                            sortField === "startDate" && prev === "asc" ? "desc" : "asc"
                        );
                    }}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none"
                    }}
                >
                    Başlama Tarihi
                    {renderSortIcon("startDate")}
                </div>
            ) as any,
            field: "dtBaslangicTarihiText",
            className: "text-center"
        },
        { header: "Bitiş Tarihi", field: "dtBitisTarihiText", className: "text-center" },
        { header: "Süresi", field: "qtSuresi", className: "text-center" },
        { header: "Açıklama", field: "chAciklama", className: "text-center" },
        { header: "Durum", field: "ctDurumAdi", className: "text-center" },
    ];

    // Tarih formatlama fonksiyonu
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            // Geçerli bir tarih kontrolü
            if (isNaN(date.getTime())) return "Bilgi Yok";

            // Türkçe format: GG.AA.YYYY
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            return `${day}.${month}.${year}`;
        } catch (error) {
            return "Bilgi Yok";
        }
    };

    useEffect(() => {
        const fetchIzinRaporlari = async () => {
            // Rate limiting kontrolü - aynı istek devam ediyorsa veya aynı sicil zaten yüklendiyse çık
            if (isLoadingRef.current) {
                return;
            }
            if (lastLoadedSicilRef.current === qtSicilId && izinRaporList.length > 0) {
                return;
            }

            setError(null);

            if (!qtSicilId) {
                setError("Sicil ID mevcut değil.");
                setLoading(false);
                return;
            }

            let ctTurValue: number;
            if (personelTipi === 'M') {
                ctTurValue = 1; // Memur
            } else if (personelTipi === 'I' || personelTipi === 'K' || personelTipi === 'T') {
                ctTurValue = 2; // İşçi / Taşeron (K ve T de dahil!)
            } else {
                ctTurValue = 1; // Varsayılan
            }

            try {
                isLoadingRef.current = true;
                setLoading(true);
                const request: PerIzinRaporBilgiListRequest = {
                    qtSicilId: qtSicilId,
                    ctTur: ctTurValue
                };
                const response = await IkIslemleriAPI.perIzinRaporBilgiList(request);

                if (response && response.data) {
                    const formattedData = response.data.map((item: any) => ({
                        ...item,
                        _dtBaslangicTs: new Date(item.dtBaslangicTarihi).getTime(),
                        _dtBitisTs: new Date(item.dtBitisTarihi).getTime(),

                        dtBaslangicTarihiText: formatDate(item.dtBaslangicTarihi),
                        dtBitisTarihiText: formatDate(item.dtBitisTarihi),
                    }));
                    setIzinRaporList(formattedData);
                    lastLoadedSicilRef.current = qtSicilId;
                } else {
                    setIzinRaporList([]);
                }
            } catch (err: any) {
                setIzinRaporList([]);
                setError("Veri çekilirken bir hata oluştu: " + (err?.message ?? err));
                console.error("İzin ve Rapor Listesi Hatası:", err);
            } finally {
                isLoadingRef.current = false;
                setLoading(false);
            }
        };

        fetchIzinRaporlari();
    }, [qtSicilId, personelTipi]);

    const colCount = 6;

    return (
        <TableComponent
            columns={columns}
            data={sortedData}
            recordsPerPage={12}
        />
    );
};

export default IzinVeRaporlar;